import type { CaseDetail } from 'common/src/api/shipment-cases';
import { useTranslation } from 'common/src/hooks/useTranslation';
import { caseActions, caseKinds, confidenceLabels, outcomes } from './model';
import CaseAttachments from './CaseAttachments';

export default function CaseHistory({
  detail,
  identity,
}: {
  detail: CaseDetail;
  identity: string;
}) {
  const { t, currentLanguage } = useTranslation('shipmentCases');
  const valuations = detail.valuations;
  const date = (value: string) =>
    new Intl.DateTimeFormat(currentLanguage === 'en' ? 'en-US' : 'es-CL', {
      dateStyle: 'medium',
      timeStyle: 'short',
      hour12: false,
    }).format(new Date(value));
  const money = (value: number) =>
    new Intl.NumberFormat(currentLanguage === 'en' ? 'en-US' : 'es-CL', {
      style: 'currency',
      currency: 'USD',
    }).format(value);
  return (
    <ol className='flex flex-col'>
      {detail.events.map((e, index) => (
        <li
          key={e.id}
          className='grid grid-cols-[20px_minmax(0,1fr)] gap-3 pb-5 last:pb-0'
        >
          <div className='relative flex justify-center'>
            <span
              className={`relative z-10 mt-1 size-3 rounded-full border-2 ${['create', 'receive_input', 'reopen'].includes(e.action) ? 'border-success bg-success' : ['assign', 'assign_input', 'responsible'].includes(e.action) ? 'border-warning bg-content1' : e.action === 'request_input' ? 'border-secondary bg-content1' : 'border-default-400 bg-content1'}`}
            />
            {index < detail.events.length - 1 && (
              <span className='absolute bottom-0 top-5 w-px bg-default-200' />
            )}
          </div>
          <div className='min-w-0'>
            <div className='flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5'>
              <p className='text-[13px] font-semibold'>
                {t(
                  caseActions[e.action as keyof typeof caseActions] || e.action
                )}
              </p>
              <p className='mt-0.5 text-[11px] text-default-400'>
                {e.actor_name} · {date(e.created_at)}
              </p>
            </div>
            {e.action === 'assign' && (
              <p className='mt-1 text-xs leading-relaxed text-default-400'>
                {e.payload.before?.owner?.name || t('Sin asignar')} →{' '}
                {e.payload.after.owner?.name || t('Sin asignar')}
              </p>
            )}
            {e.action === 'responsible' && (
              <p className='mt-1 text-xs leading-relaxed text-default-400'>
                {e.payload.before?.responsible
                  ? t(e.payload.before.responsible)
                  : t('Sin asignar')}{' '}
                →{' '}
                {e.payload.after.responsible
                  ? t(e.payload.after.responsible)
                  : t('Sin asignar')}{' '}
                {e.payload.after.responsible_name}
              </p>
            )}
            {e.action === 'escalate' && (
              <p className='mt-1 text-xs leading-relaxed text-default-400'>
                {e.payload.before && t(caseKinds[e.payload.before.kind])} →{' '}
                {t(caseKinds[e.payload.after.kind])}
              </p>
            )}
            {e.action === 'request_input' && (
              <p className='mt-1 text-xs leading-relaxed text-default-400'>
                {t('Destinatario')}: {e.payload.details.recipient} ·{' '}
                {e.payload.details.due_days} {t('días')}
              </p>
            )}
            {e.action === 'request_input' &&
              e.payload.details.followup_owner && (
                <p className='mt-1 text-xs leading-relaxed text-default-400'>
                  {t('Encargado de seguimiento')}:{' '}
                  {e.payload.details.followup_owner.name}
                </p>
              )}
            {e.action === 'request_input' &&
              e.payload.details.external_entity && (
                <p className='mt-1 text-xs leading-relaxed text-default-400'>
                  {t('Tipo de entidad')}:{' '}
                  {e.payload.details.external_entity.type}
                </p>
              )}
            {e.action === 'assign_input' && (
              <p className='mt-1 text-xs leading-relaxed text-default-400'>
                #{e.payload.input_id} ·{' '}
                {e.payload.details.previous_followup_owner?.name ||
                  t('Sin asignar')}{' '}
                → {e.payload.details.followup_owner?.name}
              </p>
            )}
            {e.action === 'close' && (
              <p className='mt-1 text-xs leading-relaxed text-default-400'>
                {e.payload.after.outcome &&
                  t(outcomes[e.payload.after.outcome])}
              </p>
            )}
            <p className='mt-1 whitespace-pre-wrap break-words text-xs leading-relaxed text-default-500'>
              {e.payload.details.note}
            </p>
            {valuations
              .filter((v) => v.event_id === e.id)
              .map((v) => (
                <p
                  key={v.id}
                  className='mt-1 text-xs leading-relaxed text-default-400'
                >
                  {money(v.amount_usd)} · {v.kilos} kg × ({v.benchmark_usd_kg} −{' '}
                  {v.obtained_usd_kg}) USD/kg ·{' '}
                  {t(confidenceLabels[v.confidence])}
                </p>
              ))}
            <CaseAttachments
              identity={identity}
              event={e}
              documents={detail.documents}
            />
          </div>
        </li>
      ))}
    </ol>
  );
}
